import { IPlayerGameStats } from "../types/types";

export interface ChampionPickStats {
  champion: string;
  games: number;
  wins: number;
  losses: number;
  winRate: number;
  avgKills: number;
  avgDeaths: number;
  avgAssists: number;
  kda: number;
  csPerMin: number;
  dpm: number;
}

const DDRAGON_VERSION = "16.13.1";

/** Display / sheet / Riot names → Data Dragon champion file id (casing is significant). */
const DDRAGON_ALIASES: Record<string, string> = {
  "Nunu & Willump": "Nunu",
  Nunu: "Nunu",
  "Renata Glasc": "Renata",
  Renata: "Renata",
  "Bel'Veth": "Belveth",
  Belveth: "Belveth",
  "K'Sante": "KSante",
  KSante: "KSante",
  "Kai'Sa": "Kaisa",
  Kaisa: "Kaisa",
  "Kha'Zix": "Khazix",
  Khazix: "Khazix",
  "Kog'Maw": "KogMaw",
  KogMaw: "KogMaw",
  "Cho'Gath": "Chogath",
  Chogath: "Chogath",
  "Dr. Mundo": "DrMundo",
  DrMundo: "DrMundo",
  "Jarvan IV": "JarvanIV",
  JarvanIV: "JarvanIV",
  "Lee Sin": "LeeSin",
  LeeSin: "LeeSin",
  "Master Yi": "MasterYi",
  MasterYi: "MasterYi",
  "Miss Fortune": "MissFortune",
  MissFortune: "MissFortune",
  "Twisted Fate": "TwistedFate",
  TwistedFate: "TwistedFate",
  "Tahm Kench": "TahmKench",
  TahmKench: "TahmKench",
  "Xin Zhao": "XinZhao",
  XinZhao: "XinZhao",
  "Aurelion Sol": "AurelionSol",
  AurelionSol: "AurelionSol",
  "Rek'Sai": "RekSai",
  RekSai: "RekSai",
  "Vel'Koz": "Velkoz",
  Velkoz: "Velkoz",
  LeBlanc: "Leblanc",
  Leblanc: "Leblanc",
  Wukong: "MonkeyKing",
  MonkeyKing: "MonkeyKing",
};

const DDRAGON_ID_BY_NORMALIZED: Record<string, string> = Object.fromEntries(
  Object.entries(DDRAGON_ALIASES).map(([name, id]) => [
    name.toLowerCase().replace(/[^a-z0-9]/g, ""),
    id,
  ]),
);

export const aggregateChampionStats = (games: IPlayerGameStats[]): ChampionPickStats[] => {
  const map = new Map<
    string,
    {
      wins: number;
      losses: number;
      kills: number;
      deaths: number;
      assists: number;
      csSum: number;
      csCount: number;
      dpmSum: number;
      dpmCount: number;
    }
  >();

  for (const game of games) {
    const champion = game.champion?.trim();
    if (!champion) continue;

    const entry = map.get(champion) ?? {
      wins: 0,
      losses: 0,
      kills: 0,
      deaths: 0,
      assists: 0,
      csSum: 0,
      csCount: 0,
      dpmSum: 0,
      dpmCount: 0,
    };
    if (game.result === "WIN") entry.wins += 1;
    else if (game.result === "DEFEAT") entry.losses += 1;

    entry.kills += game.kills ?? 0;
    entry.deaths += game.deaths ?? 0;
    entry.assists += game.assists ?? 0;
    if (game.csPerMin != null) {
      entry.csSum += game.csPerMin;
      entry.csCount += 1;
    }
    if (game.dpm != null) {
      entry.dpmSum += game.dpm;
      entry.dpmCount += 1;
    }

    map.set(champion, entry);
  }

  return [...map.entries()]
    .map(([champion, stats]) => {
      const total = stats.wins + stats.losses;
      const kda =
        stats.kills + stats.assists === 0 && stats.deaths === 0
          ? 0
          : (stats.kills + stats.assists) / Math.max(stats.deaths, 1);
      const csPerMin = stats.csCount > 0 ? stats.csSum / stats.csCount : 0;
      const dpm = stats.dpmCount > 0 ? stats.dpmSum / stats.dpmCount : 0;

      return {
        champion,
        games: total,
        wins: stats.wins,
        losses: stats.losses,
        winRate: total > 0 ? (stats.wins / total) * 100 : 0,
        avgKills: total > 0 ? stats.kills / total : 0,
        avgDeaths: total > 0 ? stats.deaths / total : 0,
        avgAssists: total > 0 ? stats.assists / total : 0,
        kda,
        csPerMin,
        dpm,
      };
    })
    .filter((row) => row.games > 0)
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate);
};

export const championIconUrl = (champion: string): string => {
  const trimmed = champion.trim();
  const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]/g, "");
  const key = Object.hasOwn(DDRAGON_ALIASES, trimmed)
    ? DDRAGON_ALIASES[trimmed]
    : Object.hasOwn(DDRAGON_ID_BY_NORMALIZED, normalized)
      ? DDRAGON_ID_BY_NORMALIZED[normalized]
      : trimmed.replace(/[^a-zA-Z0-9]/g, "");
  return `https://ddragon.leagueoflegends.com/cdn/${DDRAGON_VERSION}/img/champion/${key}.png`;
};
